import "./ThumbnailsSlider.scss"

import React, { FunctionComponent, useState, useMemo, useCallback, useEffect } from "react"
import cx from "classnames"

import { Flex } from "components/Flex"
import { Grid } from "components/Grid"

import iconGalleryArrowLeft from "assets/img/gallery-arrow-left.svg"
import iconGalleryArrowRight from "assets/img/gallery-arrow-right.svg"
import IconPlus from "./assets/icon-plus.svg?react"

import Spinner from "components/Spinner/Spinner"
import usePrevious from "hooks/usePrevious"
import { useTranslation } from "react-i18next"

interface IThumbnailsSliderProps {
    maxCountToShow: number
    images: string[]
    activeIndex: number
    onSwitchToIndex(index: number): void
    isLoadingMore?: boolean
    loadMore?(): any
    onAdd?(): any
}

const ThumbnailsSlider: FunctionComponent<React.PropsWithChildren<IThumbnailsSliderProps>> = ({
    maxCountToShow,
    images,
    activeIndex,
    onSwitchToIndex,
    isLoadingMore,
    loadMore,
    onAdd
}) => {
    const { t } = useTranslation()
    const prevActiveIndex = usePrevious<number>(activeIndex)

    const imagesIndexed: { image: string, index: number }[] = useMemo(() => {
        return images.map((image, index) => ({
            image,
            index
        }))
    }, [images])

    const [thumbnailRange, setThumbnailRange] = useState<{ start: number, end: number }>({
        start: 0,
        end: maxCountToShow
    })

    const canMoveThumbnailsRangeBackward = thumbnailRange.start > 0
    const canMoveThumbnailsRangeForward = thumbnailRange.end < images.length

    const isThumbnailNavPrevEnabled = canMoveThumbnailsRangeBackward
    const isThumbnailNavNextEnabled = (canMoveThumbnailsRangeForward || !!loadMore) && !isLoadingMore

    const visibleImages = useMemo(() => {
        return imagesIndexed.slice(thumbnailRange.start, thumbnailRange.end)
    }, [imagesIndexed, thumbnailRange])

    const moveThumbnailRangeBackward = useCallback(() => {
        if (!canMoveThumbnailsRangeBackward) return
        setThumbnailRange({
            start: thumbnailRange.start - 1,
            end: thumbnailRange.end - 1
        })
    }, [canMoveThumbnailsRangeBackward, thumbnailRange.end, thumbnailRange.start])

    const moveThumbnailRangeForward = useCallback(() => {
        if (!canMoveThumbnailsRangeForward) return
        setThumbnailRange({
            start: thumbnailRange.start + 1,
            end: thumbnailRange.end + 1
        })
    }, [canMoveThumbnailsRangeForward, thumbnailRange.end, thumbnailRange.start])

    const focusThumbnailRangeOnIndex = useCallback((index: number) => {
        if (index >= thumbnailRange.start && index < thumbnailRange.end - 1) return

        let start = 0
        let end = maxCountToShow

        const rangeCenterCandidate = maxCountToShow / 2
        start = Math.floor(index - rangeCenterCandidate)
        if (start < 0) {
            end = maxCountToShow
            start = 0
        } else {
            end = Math.ceil(index + rangeCenterCandidate)
            if (end > images.length) {
                end = images.length
                start = images.length - maxCountToShow
                if (start < 0) {
                    start = 0
                }
            }
        }

        setThumbnailRange({
            start,
            end
        })
    }, [images.length, maxCountToShow, thumbnailRange.end, thumbnailRange.start])

    const focusThumbnailRangeOnActiveIndex = useCallback(() => {
        focusThumbnailRangeOnIndex(activeIndex)
    }, [activeIndex, focusThumbnailRangeOnIndex])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(focusThumbnailRangeOnActiveIndex, [])

    const adjustThumbnailRange = useCallback((oldIndex, newIndex) => {
        if (newIndex === oldIndex) return

        if (newIndex > oldIndex) {
            if (newIndex >= thumbnailRange.end) {
                moveThumbnailRangeForward()
            }
        } else {
            if (newIndex < thumbnailRange.start) {
                moveThumbnailRangeBackward()
            }
        }
    }, [moveThumbnailRangeBackward, moveThumbnailRangeForward, thumbnailRange.end, thumbnailRange.start])

    useEffect(() => {
        if (typeof loadMore !== "function"
            || isLoadingMore
            || thumbnailRange.end < images.length) {
            return
        }

        loadMore()
    }, [images.length, isLoadingMore, loadMore, thumbnailRange.end])

    useEffect(() => {
        if (typeof prevActiveIndex === "undefined" || activeIndex > images.length  - 1) return
        adjustThumbnailRange(prevActiveIndex, activeIndex)
    }, [prevActiveIndex, activeIndex, adjustThumbnailRange, images.length])

    useEffect(() => {
        if (thumbnailRange.end > images.length) {
            const start = images.length - maxCountToShow
            setThumbnailRange({
                start: Math.max(start, 0),
                end: images.length
            })
        } else if (thumbnailRange.end - thumbnailRange.start < images.length
            && thumbnailRange.end - thumbnailRange.start < maxCountToShow) {
            setThumbnailRange({
                start: 0,
                end: Math.min(images.length, maxCountToShow)
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images.length, maxCountToShow])

    const addButton = (
        typeof onAdd === "function" ?
            <button className="thumbnails-slider__add"
                aria-label={t('addPictures.choosePicture')}
                onClick={onAdd}>
                <IconPlus />
            </button>
        : null
    )

    return (
        <Flex alignItems="center"
            justifyContent="center">
            <div className="thumbnails-slider">
                <button
                    type="button"
                    data-testid="thumb-prev-btn"
                    className="thumbnails-slider__nav"
                    disabled={!isThumbnailNavPrevEnabled}
                    onClick={moveThumbnailRangeBackward}>
                    {isThumbnailNavPrevEnabled &&
                        <img src={iconGalleryArrowLeft} alt="" />
                    }
                </button>
                <Grid className="thumbnails-slider__list"
                    columns={`repeat(${visibleImages.length + (images.length <= maxCountToShow && addButton ? 1 : 0)}, 1fr)`}
                    gap=".8rem">
                    {visibleImages.map(item => (
                        <div key={item.index} className={cx("thumbnails-slider__thumb", { "thumbnails-slider__thumb--active": item.index === activeIndex })}>
                            <button
                                type="button"
                                className="thumbnails-slider__thumb-btn"
                                data-testid="thumb-btn"
                                disabled={item.index === activeIndex}
                                onClick={() => onSwitchToIndex(item.index)} />
                            <img
                                src={item.image}
                                className="thumbnails-slider__thumb-img"
                                data-testid="thumb-img"
                                alt="" />
                        </div>
                    ))}
                    {images.length <= maxCountToShow && addButton}
                </Grid>
                <button
                    type="button"
                    data-testid="thumb-next-btn"
                    className="thumbnails-slider__nav"
                    disabled={!isThumbnailNavNextEnabled}
                    onClick={moveThumbnailRangeForward}>
                    {isThumbnailNavNextEnabled &&
                        <img src={iconGalleryArrowRight} alt="" />
                    }
                    {isLoadingMore &&
                        <Spinner />
                    }
                </button>
            </div>
            {images.length > maxCountToShow &&
                <div className="ml-4">
                    {addButton}
                </div>
            }
        </Flex>
    )
}

export default ThumbnailsSlider
